import type { Activity, Learner, SubjectKey } from '../types/domain';

export const AUSTRALIAN_CURRICULUM = {
  name: 'Australian Curriculum',
  version: '9.0',
  sourceUrl: 'https://www.australiancurriculum.edu.au/',
  attribution: '© Australian Curriculum, Assessment and Reporting Authority (ACARA) 2010 to present. Australian Curriculum material is used and adapted under CC BY 4.0 unless otherwise indicated.',
  disclaimer: 'MyLearningWorld is not affiliated with, sponsored by or endorsed by ACARA.',
  dimensions: {
    learningAreas: [
      {
        id:'english', name:'English', years:'Foundation–Year 10',
        strands:['Language','Literature','Literacy'],
        summary:'Develops reading, viewing, listening, speaking, writing, creating and critical engagement with increasingly complex texts.'
      },
      {
        id:'mathematics', name:'Mathematics', years:'Foundation–Year 10',
        strands:['Number','Algebra','Measurement','Space','Statistics','Probability'],
        summary:'Builds mathematical understanding, fluency, reasoning and problem-solving across six interrelated strands.'
      },
      {
        id:'science', name:'Science', years:'Foundation–Year 10',
        strands:['Science understanding','Science as a human endeavour','Science inquiry'],
        summary:'Builds scientific knowledge, evidence-based inquiry, prediction, investigation and understanding of science in society.'
      },
      {
        id:'hass', name:'Humanities and Social Sciences', years:'Foundation–Year 10',
        strands:['Knowledge and understanding','Skills'],
        summary:'Includes HASS in F–6, then History, Geography, Civics and Citizenship, and Economics and Business in later years.'
      },
      {
        id:'technologies', name:'Technologies', years:'Foundation–Year 10',
        strands:['Knowledge and understanding','Processes and production skills'],
        summary:'Includes Design and Technologies and Digital Technologies, with design, systems and computational thinking.'
      },
      {
        id:'health', name:'Health and Physical Education', years:'Foundation–Year 10',
        strands:['Personal, social and community health','Movement and physical activity'],
        summary:'Develops health literacy, wellbeing, relationships, movement skills and participation in physical activity.'
      },
      {
        id:'arts', name:'The Arts', years:'Foundation–Year 10',
        strands:['Exploring and responding','Developing practices and skills','Creating and making','Presenting and performing'],
        summary:'Includes Dance, Drama, Media Arts, Music and Visual Arts.'
      },
      {
        id:'languages', name:'Languages', years:'Foundation–Year 10',
        strands:['Communicating','Understanding'],
        summary:'Develops communication in another language and understanding of language, culture and identity.'
      }
    ],
    generalCapabilities: [
      'Critical and Creative Thinking','Digital Literacy','Ethical Understanding','Intercultural Understanding',
      'Literacy','Numeracy','Personal and Social capability'
    ],
    crossCurriculumPriorities: [
      'Aboriginal and Torres Strait Islander Histories and Cultures',
      'Asia and Australia’s Engagement with Asia',
      'Sustainability'
    ]
  }
} as const;

export type CurriculumAlignment = {
  learningArea:string;
  strand:string;
  capabilities:string[];
  priorities:string[];
  yearLevel:number;
};

function yearFromLearner(learner:Learner){
  const m=String(learner.school_grade||'').match(/\d+/);
  return Math.max(1,Math.min(10,Number(m?.[0]||3)));
}

export function subjectArea(subject:SubjectKey){
  if(subject==='reading'||subject==='writing') return 'English';
  if(subject==='maths') return 'Mathematics';
  if(subject==='science') return 'Science';
  if(subject==='humanities') return 'Humanities and Social Sciences';
  return 'Technologies';
}

export function alignmentForActivity(activity:Activity, learner:Learner):CurriculumAlignment {
  const skills=(activity.skillTags||[]).map(x=>x.toLowerCase());
  const text=skills.join(' ');
  let learningArea='English', strand='Literacy';
  if(/addition|subtraction|multiplication|division|fractions|money|number/.test(text)){learningArea='Mathematics';strand='Number'}
  else if(/measurement|time/.test(text)){learningArea='Mathematics';strand='Measurement'}
  else if(/geometry|space/.test(text)){learningArea='Mathematics';strand='Space'}
  else if(/data|statistics/.test(text)){learningArea='Mathematics';strand='Statistics'}
  else if(/probability|chance/.test(text)){learningArea='Mathematics';strand='Probability'}
  else if(/algebra|pattern/.test(text)){learningArea='Mathematics';strand='Algebra'}
  else if(/science|forces|motion|energy|habitat|living|earth/.test(text)){learningArea='Science';strand=/research|reasoning|investig/.test(text)?'Science inquiry':'Science understanding'}
  else if(/engineering|coding|programming|logic|design/.test(text)){learningArea='Technologies';strand='Processes and production skills'}
  else if(/history|geography|civics|economics|research|sequencing/.test(text)){learningArea='Humanities and Social Sciences';strand=/research|sequencing/.test(text)?'Skills':'Knowledge and understanding'}
  else if(/writing|creating/.test(text)){learningArea='English';strand='Literacy'}
  else if(/reading|vocabulary/.test(text)){learningArea='English';strand='Literacy'}

  const capabilities:string[]=[];
  if(learningArea==='English') capabilities.push('Literacy');
  if(learningArea==='Mathematics') capabilities.push('Numeracy');
  if(learningArea==='Technologies') capabilities.push('Digital Literacy');
  if(/reasoning|problem solving|logic|research|design|creativity/.test(text)) capabilities.push('Critical and Creative Thinking');
  if(/collaboration|communication/.test(text)) capabilities.push('Personal and Social capability');

  const priorities:string[]=[];
  if((activity.interestTags||[]).some(x=>/nature|animals|space|environment|farm|tractors/i.test(x))) priorities.push('Sustainability');

  return {learningArea,strand,capabilities:[...new Set(capabilities)],priorities:[...new Set(priorities)],yearLevel:yearFromLearner(learner)};
}

export function curriculumCoverage(attempts:Array<{skill_tags?:string[];interest_tags?:string[]}>, learner:Learner){
  const synthetic=(a:any):Activity=>({
    id:'coverage',title:'',icon:'',interestTags:a.interest_tags||[],skillTags:a.skill_tags||[],difficulty:.5,minutes:0,type:'text',story:'',question:'',answer:'',explanation:''
  });
  const map=new Map<string,{area:string;strand:string;count:number}>();
  for(const a of attempts){
    const x=alignmentForActivity(synthetic(a),learner);
    const key=x.learningArea+'|'+x.strand;
    const row=map.get(key)||{area:x.learningArea,strand:x.strand,count:0};
    row.count++;map.set(key,row);
  }
  return [...map.values()].sort((a,b)=>b.count-a.count);
}

export const CASE_MODEL = [
  {name:'Curriculum',detail:'Plan from the Australian Curriculum and adjust where needed.'},
  {name:'Abilities',detail:'Take account of the learner’s abilities, interests and experiences.'},
  {name:'Standards',detail:'Use achievement standards to maintain high expectations and guide progress.'},
  {name:'Evaluation',detail:'Review whether adjustments improve access, participation and progress.'}
] as const;
